import React, { useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ServiceHero,
  AnimatedBreadcrumb,
  ProcessStepper,
  // TechStackGrid, // Hidden for now
  // RelatedServicesCarousel, // Hidden for now
  StickyBookBar,
} from '../components/services';
import {
  GuaranteeSection,
  CTASection,
} from '../components/landing';
import { PellagoDashboardDemo } from '../components/demos/PellagoDashboardDemo';
import { PropertyBaseAdminDemo } from '../components/demos/PropertyBaseAdminDemo';

// ============================================
// Software Backlog Problem Section
// ============================================
const BacklogProblemSection: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const problems = [
    {
      text: 'That internal tool request? Been sitting in the backlog for 8 months.',
      subtext: 'Your engineers keep saying "next quarter." Next quarter never comes.',
    },
    {
      text: 'The MVP you need for the funding round? Still in "planning."',
      subtext: "The round closes in 6 weeks. You're going to pitch with mockups instead of a working product.",
    },
    {
      text: 'The dashboard the CEO has been asking about since January?',
      subtext: 'Still "in progress." Every board meeting, same question. Same answer.',
    },
    {
      text: 'The customer portal that would reduce support tickets by 40%?',
      subtext: 'Deprioritized. Again. Because "the team is busy."',
    },
    {
      text: 'That spreadsheet-based workflow everyone hates?',
      subtext: 'The one with 47 tabs that breaks every month? Still a spreadsheet.',
    },
    {
      text: 'The feature your competitor shipped last month?',
      subtext: "You haven't started. They're pulling ahead. Every day the gap widens.",
    },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 via-transparent to-transparent" />
      <motion.div
        className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[150px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-red-500/10 border border-red-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            <span className="text-red-400 text-sm font-medium">The Problem</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            The Software You Need Is Stuck in a Backlog
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400"
          >
            Here's what that's costing you.
          </motion.p>
        </div>

        {/* Problem accordion */}
        <div className="space-y-4 mb-16">
          {problems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="rounded-2xl bg-zinc-900/50 border-l-4 border-red-500/50 hover:border-red-500 transition-colors overflow-hidden"
            >
              <button
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="w-full p-5 text-left flex items-center justify-between gap-4"
              >
                <p className="font-semibold text-white">{item.text}</p>
                <motion.div
                  animate={{ rotate: expandedIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0 w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </motion.div>
              </button>
              <AnimatePresence>
                {expandedIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="px-5 pb-5 text-zinc-500 text-sm">{item.subtext}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Cost breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-r from-red-500/30 via-orange-500/30 to-red-500/30 rounded-2xl" />
          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-white mb-8">
              Let's do the math on what that backlog is costing you:
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                { value: '$15K/mo', label: 'Manual workarounds for missing tools' },
                { value: '$8K/mo', label: 'Avoidable support costs' },
                { value: '∞', label: 'Competitive disadvantage' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <p className="text-3xl font-bold text-red-400">{stat.value}</p>
                  <p className="text-zinc-500 text-sm mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-lg font-semibold text-zinc-300">
              You don't have a development problem. You have a <span className="text-red-400">speed problem.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// AI-Powered Development Comparison
// ============================================

// Icons for comparison section
const ClockSlowIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const BoltIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
  </svg>
);

const XCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const AIPoweredDevelopmentSection: React.FC = () => {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const traditionalItems = [
    'Write authentication from scratch',
    'Build database schemas manually',
    'Code API endpoints one by one',
    'Create form validation by hand',
    'Style responsive layouts pixel by pixel',
  ];

  const aiPoweredItems = [
    'Auth templates ready instantly',
    'Schemas generated from description',
    'APIs built in minutes, not days',
    'Validation logic auto-generated',
    'Responsive design by default',
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-transparent to-zinc-900/30" />
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700/30 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500"></span>
            <span className="text-zinc-400 text-sm">The Difference</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            80% of Development is Boilerplate
          </h2>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            AI eliminates the repetitive work. You focus on what makes your app unique.
          </p>
        </motion.div>

        {/* Comparison cards */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {/* Traditional Development */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-zinc-800/50">
                <div className="w-12 h-12 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center">
                  <ClockSlowIcon className="w-6 h-6 text-zinc-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Traditional Development</h3>
                  <p className="text-zinc-500 text-sm">The old way</p>
                </div>
              </div>

              {/* Items */}
              <ul className="space-y-4 mb-6">
                {traditionalItems.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-zinc-800/50 flex items-center justify-center mt-0.5">
                      <XCircleIcon className="w-3.5 h-3.5 text-zinc-600" />
                    </div>
                    <span className="text-zinc-500 text-sm">{item}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Result */}
              <div className="p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/20">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500 text-sm">Timeline</span>
                  <span className="text-white font-semibold">4 months</span>
                </div>
                <div className="mt-3 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full w-full bg-zinc-600 rounded-full" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* AI-Powered Development */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative group"
          >
            {/* Highlight border */}
            <div className="absolute -inset-[1px] bg-gradient-to-b from-violet-500/30 to-transparent rounded-2xl" />

            <div className="relative h-full bg-violet-500/5 backdrop-blur-sm rounded-2xl border border-violet-500/20 p-8">
              {/* Recommended badge */}
              <div className="absolute -top-3 right-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20">
                  <BoltIcon className="w-3.5 h-3.5 text-violet-400" />
                  <span className="text-violet-400 text-xs font-medium">8x Faster</span>
                </div>
              </div>

              {/* Header */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-violet-500/10">
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                  <BoltIcon className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">AI-Powered Development</h3>
                  <p className="text-violet-400/70 text-sm">The VibeCode way</p>
                </div>
              </div>

              {/* Items */}
              <ul className="space-y-4 mb-6">
                {aiPoweredItems.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-500/10 flex items-center justify-center mt-0.5">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-violet-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">{item}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Result */}
              <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <div className="flex items-center justify-between">
                  <span className="text-violet-400/70 text-sm">Timeline</span>
                  <span className="text-white font-semibold">2 weeks</span>
                </div>
                <div className="mt-3 h-2 bg-violet-500/20 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-violet-500 to-violet-400 rounded-full"
                    initial={{ width: 0 }}
                    animate={isInView ? { width: '12.5%' } : {}}
                    transition={{ duration: 1, delay: 0.8 }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8"
        >
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-white mb-1">1</p>
              <p className="text-zinc-500 text-sm">VibeCode developer</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-violet-400 mb-1">=</p>
              <p className="text-zinc-500 text-sm">output of</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white mb-1">5</p>
              <p className="text-zinc-500 text-sm">traditional engineers</p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-zinc-800/50 text-center">
            <p className="text-zinc-300">
              <span className="text-white font-semibold">This isn't cutting corners.</span>{' '}
              It's eliminating work that never needed to exist.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// What We Build Grid
// ============================================
const WhatWeBuildSection: React.FC = () => {
  const apps = [
    {
      title: 'Internal Operations Dashboards',
      speed: '5 days',
      description: 'Real-time data. Custom filters. Export functionality. User permissions. Everything your ops team actually needs.',
    },
    {
      title: 'Customer-Facing Portals',
      speed: '2 weeks',
      description: 'Account management. Order history. Support tickets. Document uploads. Branded, mobile-responsive, integrated.',
    },
    {
      title: 'MVPs for Fundraising',
      speed: '2-3 weeks',
      description: 'Ship a working product before your pitch deck is done. Real users. Real data. Real traction.',
    },
    {
      title: 'Data Visualization Tools',
      speed: 'Days',
      description: 'Connect your data sources. Build custom views. Set up automated reports. No more exporting to Excel.',
    },
    {
      title: 'Workflow Automation Apps',
      speed: '1 week',
      description: 'That process your team does in spreadsheets? Form inputs. Approval workflows. Notifications. Audit trails.',
    },
    {
      title: 'Admin Panels & CMS',
      speed: '7-10 days',
      description: "Custom admin tools that fit YOUR workflow. Not another generic CMS that forces you into someone else's process.",
    },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      <motion.div
        className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[120px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-violet-500/10 border border-violet-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-violet-400 text-sm font-medium">What We Build</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white"
          >
            Real Apps. Real Fast.
          </motion.h2>
        </div>

        {/* Apps grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-violet-500/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">{item.title}</h3>
                <span className="text-sm font-semibold text-violet-400 bg-violet-500/10 px-3 py-1 rounded-full border border-violet-500/20">
                  {item.speed}
                </span>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// Portfolio Showcase Section
// ============================================
const PortfolioShowcaseSection: React.FC = () => {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const [activeProject, setActiveProject] = useState<'pellago' | 'propertybase'>('pellago');

  const projects = {
    pellago: {
      id: 'pellago',
      title: 'Property Management Platform',
      client: 'Pellago / Investland Bali',
      timeline: '3 weeks',
      description: 'Full-stack property management system with multi-tenant architecture, real-time analytics, and automated financial reporting.',
      features: [
        'Multi-property dashboard',
        'Tenant & lease management',
        'Automated invoicing',
        'Expense tracking',
        'Maintenance scheduling',
        'Financial reporting',
      ],
      techStack: ['Next.js 16', 'React 19', 'Prisma', 'PostgreSQL', 'Tailwind CSS'],
      demo: PellagoDashboardDemo,
    },
    propertybase: {
      id: 'propertybase',
      title: 'Real Estate SaaS Admin',
      client: 'PropertyBase.ai',
      timeline: '2 weeks',
      description: 'Multi-tenant real estate SaaS platform with project management, lead tracking, investor portal, and commission calculations.',
      features: [
        'Project & unit management',
        'Lead tracking CRM',
        'Team collaboration',
        'Financial analytics',
        'Investor dashboard',
        'Commission tracking',
      ],
      techStack: ['React', 'Vite', 'shadcn/ui', 'Tailwind CSS', 'TypeScript'],
      demo: PropertyBaseAdminDemo,
    },
  };

  const currentProject = projects[activeProject];
  const DemoComponent = currentProject.demo;

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background effects */}
      <motion.div
        className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-violet-500/5 rounded-full blur-[150px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-violet-500/10 border border-violet-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-violet-400 text-sm font-medium">Live Portfolio</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            See What We Actually Build
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-zinc-400 max-w-2xl mx-auto"
          >
            Interactive demos of real projects we've shipped. No mockups. No screenshots. Actual working software.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25 }}
            className="text-sm text-zinc-500 mt-3"
          >
            * Simplified demo versions for showcase purposes. Actual deployments include full backend integration, authentication, and database connectivity.
          </motion.p>
        </div>

        {/* Project Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.25 }}
          className="flex justify-center gap-3 mb-8"
        >
          {Object.values(projects).map((project) => (
            <button
              key={project.id}
              onClick={() => setActiveProject(project.id as 'pellago' | 'propertybase')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                activeProject === project.id
                  ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/25'
                  : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-300 border border-zinc-700/50'
              }`}
            >
              {project.title}
            </button>
          ))}
        </motion.div>

        {/* Demo Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="relative"
        >
          {/* Gradient border */}
          <div className="absolute -inset-[1px] bg-gradient-to-r from-violet-500/30 via-purple-500/30 to-violet-500/30 rounded-3xl" />

          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-3xl p-6 lg:p-8">
            {/* Project Header */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProject}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8"
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-white">{currentProject.title}</h3>
                    <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                      Production
                    </span>
                  </div>
                  <p className="text-zinc-400 mb-4">{currentProject.description}</p>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500">Client:</span>
                      <span className="text-white font-medium">{currentProject.client}</span>
                    </div>
                    <span className="text-zinc-700">|</span>
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500">Built in:</span>
                      <span className="text-violet-400 font-medium">{currentProject.timeline}</span>
                    </div>
                  </div>
                </div>

                {/* Tech Stack Pills */}
                <div className="flex flex-wrap gap-2">
                  {currentProject.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1.5 bg-zinc-800/50 border border-zinc-700/50 text-zinc-300 text-xs font-medium rounded-lg"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Interactive Demo */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProject}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <DemoComponent />
              </motion.div>
            </AnimatePresence>

            {/* Features Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProject}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-8 pt-6 border-t border-zinc-800"
              >
                <p className="text-sm font-medium text-zinc-400 mb-4">Key Features Delivered</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {currentProject.features.map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-3 py-2 bg-zinc-800/30 rounded-lg"
                    >
                      <svg className="w-4 h-4 text-violet-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs text-zinc-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-zinc-400 mb-4">
            This is the level of quality we deliver. Every time.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-violet-500 hover:bg-violet-600 text-white font-semibold rounded-xl transition-colors"
          >
            Build Something Like This
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Case Study Section
// ============================================

// Icons for case study section
const BuildingOfficeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
  </svg>
);

const RocketLaunchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
  </svg>
);

const CaseStudySection: React.FC = () => {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const comparisonMetrics = [
    { label: 'Cost', agency: '$120,000', ours: 'Fraction', savings: '85% less' },
    { label: 'Timeline', agency: '4 months', ours: '11 days', savings: '91% faster' },
    { label: 'Status', agency: 'Still scoping', ours: 'Deployed', savings: 'Live' },
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            <span className="text-emerald-400 text-sm font-medium">Real Results</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            See the Speed
          </h2>
          <p className="text-xl text-zinc-400">
            A real client. A real project. Real savings.
          </p>
        </motion.div>

        {/* Video placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl aspect-video flex items-center justify-center mb-12"
        >
          <div className="text-center">
            <motion.div
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-zinc-800/50 flex items-center justify-center"
              whileHover={{ scale: 1.1, backgroundColor: "rgba(139, 92, 246, 0.2)" }}
            >
              <svg className="w-8 h-8 text-zinc-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </motion.div>
            <p className="text-lg text-zinc-300">Watch us build a complete internal tool from scratch in 20 minutes</p>
            <p className="text-zinc-500 text-sm mt-2">[Video Demo Coming Soon]</p>
          </div>
        </motion.div>

        {/* Case Study Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-b from-emerald-500/30 via-violet-500/20 to-transparent rounded-3xl" />

          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-3xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500/10 via-violet-500/10 to-emerald-500/10 px-8 py-6 border-b border-zinc-800/50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium">
                      Case Study
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">The 85% Cost Cut</h3>
                  <p className="text-zinc-400 mt-1">Customer portal with full feature parity</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-violet-400">
                      85%
                    </p>
                    <p className="text-zinc-500 text-sm">Under agency quote</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Comparison Cards */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                {/* Agency Quote */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="relative"
                >
                  <div className="h-full bg-zinc-800/30 rounded-2xl border border-zinc-700/30 p-6">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-6 pb-4 border-b border-zinc-700/30">
                      <div className="w-12 h-12 rounded-xl bg-zinc-700/30 flex items-center justify-center">
                        <BuildingOfficeIcon className="w-6 h-6 text-zinc-500" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white">Agency Quote</h4>
                        <p className="text-zinc-500 text-sm">Traditional approach</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-center mb-6">
                      <p className="text-4xl font-bold text-zinc-400 line-through decoration-red-500/50">$120,000</p>
                      <p className="text-xl text-zinc-500 mt-1">4 months timeline</p>
                    </div>

                    {/* Details */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm">
                        <XCircleIcon className="w-4 h-4 text-zinc-600" />
                        <span>8-week discovery phase</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-500 text-sm">
                        <XCircleIcon className="w-4 h-4 text-zinc-600" />
                        <span>Additional costs for changes</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-500 text-sm">
                        <XCircleIcon className="w-4 h-4 text-zinc-600" />
                        <span>"Standard enterprise timeline"</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Our Build */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="relative group"
                >
                  {/* Highlight border */}
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-violet-500/40 to-emerald-500/20 rounded-2xl" />

                  <div className="relative h-full bg-gradient-to-br from-violet-500/10 to-emerald-500/5 rounded-2xl border border-violet-500/20 p-6">
                    {/* Recommended badge */}
                    <div className="absolute -top-3 right-6">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                        <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400 text-xs font-medium">Delivered</span>
                      </div>
                    </div>

                    {/* Header */}
                    <div className="flex items-center gap-4 mb-6 pb-4 border-b border-violet-500/10">
                      <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                        <RocketLaunchIcon className="w-6 h-6 text-violet-400" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white">Our Build</h4>
                        <p className="text-violet-400/70 text-sm">VibeCode approach</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-center mb-6">
                      <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-emerald-400">Scoped to you</p>
                      <p className="text-xl text-white mt-2">11 days to production</p>
                    </div>

                    {/* Details */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                        <span>Production-deployed and working</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                        <span>Same-day iteration on feedback</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                        <span>Full documentation included</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Comparison metrics bar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="grid grid-cols-3 gap-4 mb-8"
              >
                {comparisonMetrics.map((metric, index) => (
                  <div key={index} className="bg-zinc-800/30 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">{metric.label}</p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-zinc-500 text-sm line-through">{metric.agency}</span>
                      <ArrowRightIcon className="w-4 h-4 text-violet-400" />
                      <span className="text-white font-semibold">{metric.ours}</span>
                    </div>
                    <p className="text-emerald-400 text-xs font-medium mt-1">{metric.savings}</p>
                  </div>
                ))}
              </motion.div>

              {/* Bottom summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="relative"
              >
                <div className="absolute -inset-[1px] bg-gradient-to-r from-emerald-500/20 via-violet-500/20 to-emerald-500/20 rounded-xl" />
                <div className="relative bg-zinc-800/50 rounded-xl p-6 text-center">
                  <p className="text-xl text-zinc-300 mb-2">Same functionality. Same quality. Same outcome.</p>
                  <div className="flex items-center justify-center gap-6 mt-4">
                    <div>
                      <p className="text-3xl font-bold text-emerald-400">85%</p>
                      <p className="text-zinc-500 text-sm">under agency quote</p>
                    </div>
                    <div className="w-px h-12 bg-zinc-700" />
                    <div>
                      <p className="text-3xl font-bold text-violet-400">3.5mo</p>
                      <p className="text-zinc-500 text-sm">faster</p>
                    </div>
                  </div>
                  <p className="text-zinc-500 text-sm mt-4">This isn't an exception. This is what we do.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Urgency Section
// ============================================

// Icons for urgency section
const TrendingUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);

const TrendingDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />
  </svg>
);

const SparklesIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const UrgencySection: React.FC = () => {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const gapComparison = [
    { label: 'Feature releases', traditional: '4/year', aiPowered: '20+/year' },
    { label: 'Time to market', traditional: 'Months', aiPowered: 'Weeks' },
    { label: 'Development cost', traditional: 'Baseline', aiPowered: '-70%' },
    { label: 'Iteration speed', traditional: '2-week sprints', aiPowered: 'Same-day' },
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-gradient-to-r from-violet-600/10 via-purple-600/10 to-violet-600/10 rounded-full blur-[150px]"
          animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.6, 0.4] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
            <span className="text-amber-400 text-sm font-medium">The Competitive Reality</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            The Speed Gap Is the New Competitive Moat
          </h2>

          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Your competitors are shipping features in <span className="text-white font-semibold">days</span> that used to take <span className="text-white font-semibold">months.</span>
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {/* Traditional approach */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-zinc-800/50">
                <div className="w-12 h-12 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center">
                  <TrendingDownIcon className="w-6 h-6 text-zinc-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Traditional Development</h3>
                  <p className="text-zinc-500 text-sm">2-week sprint cycles</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-4">
                {gapComparison.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30"
                  >
                    <span className="text-zinc-500 text-sm">{item.label}</span>
                    <span className="text-zinc-400 font-medium">{item.traditional}</span>
                  </motion.div>
                ))}
              </div>

              {/* Bottom note */}
              <div className="mt-6 pt-6 border-t border-zinc-800/50">
                <p className="text-zinc-500 text-sm text-center">
                  Still hiring more engineers to go faster
                </p>
              </div>
            </div>
          </motion.div>

          {/* AI-powered approach */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative group"
          >
            {/* Highlight border */}
            <div className="absolute -inset-[1px] bg-gradient-to-b from-violet-500/30 to-transparent rounded-2xl" />

            <div className="relative h-full bg-violet-500/5 backdrop-blur-sm rounded-2xl border border-violet-500/20 p-8">
              {/* Badge */}
              <div className="absolute -top-3 right-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20">
                  <SparklesIcon className="w-3.5 h-3.5 text-violet-400" />
                  <span className="text-violet-400 text-xs font-medium">The Future</span>
                </div>
              </div>

              {/* Header */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-violet-500/10">
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                  <TrendingUpIcon className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">AI-Powered Development</h3>
                  <p className="text-violet-400/70 text-sm">Same-day iteration</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-4">
                {gapComparison.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.05 }}
                    className="flex items-center justify-between p-3 rounded-lg bg-violet-500/10"
                  >
                    <span className="text-violet-400/70 text-sm">{item.label}</span>
                    <span className="text-white font-semibold">{item.aiPowered}</span>
                  </motion.div>
                ))}
              </div>

              {/* Bottom note */}
              <div className="mt-6 pt-6 border-t border-violet-500/10">
                <p className="text-violet-400 text-sm text-center font-medium">
                  Building smarter, not harder
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* iPhone moment card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-r from-violet-500/30 via-purple-500/30 to-violet-500/30 rounded-2xl" />
          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/20 flex items-center justify-center">
                <SparklesIcon className="w-8 h-8 text-violet-400" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Software development just had its iPhone moment.
                </h3>
                <p className="text-lg text-zinc-400">
                  The companies that adopt AI-powered development now will outship everyone else for the next 5 years. They'll build more, test more, learn more, and win more.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom urgency */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-amber-500/10 border border-amber-500/20">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
            </span>
            <p className="text-amber-400 font-semibold text-lg">
              The only question is which side of that gap you want to be on.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Investment Comparison Section
// ============================================
const InvestmentSection: React.FC = () => {
  const options = [
    {
      title: 'Traditional Agency',
      cost: '$80K - $200K',
      time: '3-6 months',
      risk: 'High',
      riskColor: 'red',
      details: 'Scope creep, delays, change orders',
    },
    {
      title: 'In-House Team',
      cost: '$300K+/year',
      time: 'Ongoing',
      risk: 'Very High',
      riskColor: 'red',
      details: 'Hiring, management, turnover',
    },
    {
      title: 'Freelancer',
      cost: '$20K - $50K',
      time: '2-4 months',
      risk: 'Very High',
      riskColor: 'red',
      details: 'Quality, reliability, disappears',
    },
    {
      title: 'The 2-Week Build',
      cost: 'Scoped to you',
      time: '1-4 weeks',
      risk: 'Low',
      riskColor: 'green',
      details: 'Guarantee-backed',
      highlight: true,
    },
  ];

  const valueItems = [
    { item: 'Discovery & Scoping Session', value: 'Included' },
    { item: 'Custom UI/UX Design', value: 'Included' },
    { item: 'Full-Stack Development', value: 'Included' },
    { item: 'Database Architecture', value: 'Included' },
    { item: 'API Integrations', value: 'Included' },
    { item: 'Testing & QA', value: 'Included' },
    { item: 'Production Deployment', value: 'Included' },
    { item: 'Documentation & Handoff', value: 'Included' },
    { item: '30-Day Support', value: 'Included' },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-violet-500/5 rounded-full blur-[150px]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-12"
        >
          Compare Your Options
        </motion.h2>

        {/* Options grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {options.map((option, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className={`rounded-2xl p-6 ${
                option.highlight
                  ? 'bg-gradient-to-br from-violet-500/20 to-purple-500/20 border-2 border-violet-500/50'
                  : 'bg-zinc-900/50 border border-zinc-800/50'
              }`}
            >
              <h3 className="font-bold text-white mb-4">{option.title}</h3>
              <p className="text-2xl font-bold text-white mb-1">{option.cost}</p>
              <p className="text-sm text-zinc-500 mb-3">{option.time}</p>
              <div className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                option.riskColor === 'green'
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                {option.risk} Risk
              </div>
              <p className="text-xs text-zinc-500 mt-2">{option.details}</p>
            </motion.div>
          ))}
        </div>

        {/* Value stack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-b from-violet-500/50 via-purple-500/30 to-violet-500/10 rounded-3xl" />
          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-3xl p-8">
            <h3 className="text-xl font-bold text-white text-center mb-6">What's Included</h3>

            <div className="grid md:grid-cols-2 gap-3 mb-8">
              {valueItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.03 }}
                  className="flex justify-between items-center bg-zinc-800/30 rounded-lg px-4 py-3"
                >
                  <span className="text-zinc-300">{item.item}</span>
                  <span className="text-violet-400 text-sm font-medium">{item.value}</span>
                </motion.div>
              ))}
            </div>

            <div className="text-center pt-6 border-t border-zinc-800">
              <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
                Your Investment: Scoped to Your Build
              </p>
              <p className="text-zinc-400 text-sm mt-3 max-w-xl mx-auto">
                Final scope depends on project size, integrations, and complexity. We quote within 24 hours of a free discovery session — no pressure, no obligation.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 mt-6 bg-gradient-to-r from-violet-500 to-purple-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-violet-500/25 transition-all"
              >
                Book Your Free Discovery Session
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Main Page Component
// ============================================
export const VibeCodePage: React.FC = () => {
  // Process steps for the stepper
  const processSteps = [
    {
      title: 'You Tell Us',
      description: 'One kickoff call. You describe what you want. We scope it on the spot.',
      duration: '30 min',
    },
    {
      title: 'We Build It',
      description: 'Daily progress updates. Working software you can click. Every day, something new works.',
      duration: 'Days 1-10',
    },
    {
      title: 'You Test It',
      description: 'Same-day changes. Not 2-week sprint cycles. "Move this button?" Done.',
      duration: 'Ongoing',
    },
    {
      title: 'We Deploy',
      description: 'Live. Production-ready. Documented. You own the code. We hand you the keys.',
      duration: 'Day 10-14',
    },
  ];

  // Tech stack - Hidden for now
  // const techStack = [
  //   { name: 'React', category: 'Frontend' },
  //   { name: 'Next.js', category: 'Framework' },
  //   { name: 'TypeScript', category: 'Language' },
  //   { name: 'Tailwind CSS', category: 'Styling' },
  //   { name: 'Node.js', category: 'Backend' },
  //   { name: 'PostgreSQL', category: 'Database' },
  //   { name: 'Prisma', category: 'ORM' },
  //   { name: 'Vercel', category: 'Hosting' },
  //   { name: 'AWS', category: 'Cloud' },
  //   { name: 'Stripe', category: 'Payments' },
  //   { name: 'Auth0', category: 'Auth' },
  //   { name: 'OpenAI', category: 'AI' },
  // ];

  // Related services - hidden for now
  // const relatedServices = [
  //   {
  //     title: 'AI Profit Audit',
  //     description: 'Discover your highest-ROI AI opportunities',
  //     href: '/services/ai-profit-audit',
  //     accent: 'emerald' as const,
  //   },
  //   {
  //     title: 'AI Implementation',
  //     description: 'Full production AI systems in 4-12 weeks',
  //     href: '/services/ai-implementation',
  //     accent: 'blue' as const,
  //   },
  // ];

  return (
    <main className="bg-[#0a0a0a]">
      {/* Sticky Book Bar */}
      <StickyBookBar
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        accent="violet"
      />

      {/* Breadcrumb */}
      <div className="pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedBreadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Services', href: '/services' },
              { label: 'Vibe Coding', href: '/services/vibe-code' },
            ]}
            accent="violet"
          />
        </div>
      </div>

      {/* Hero Section */}
      <ServiceHero
        eyebrow="For product leaders and founders who are tired of waiting 4 months for software that should take 4 weeks"
        title="We Build Production-Ready Apps in 2 Weeks That Your Team Quoted 4 Months For"
        subtitle="Rapid Development"
        description="And yes, it's production-grade. Tested. Documented. Deployed. Real software your team can use the day we hand it off."
        metrics={[
          { value: '2', label: 'Weeks Average' },
          { value: '85%', label: 'Cost Savings' },
          { value: '100+', label: 'Apps Shipped' },
        ]}
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        accent="violet"
      />

      {/* The Problem Section */}
      <BacklogProblemSection />

      {/* AI-Powered Development */}
      <AIPoweredDevelopmentSection />

      {/* What We Build */}
      <WhatWeBuildSection />

      {/* Portfolio Showcase */}
      <PortfolioShowcaseSection />

      {/* The Process */}
      <section className="py-16 sm:py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-violet-500/10 border border-violet-500/20"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
              <span className="text-violet-400 text-sm font-medium">The Process</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              Your Total Time Investment
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400"
            >
              2-3 Hours
            </motion.p>
          </div>

          <ProcessStepper
            steps={processSteps}
            accent="violet"
            autoAdvance={true}
            autoAdvanceInterval={4000}
          />

          {/* Bottom note */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 text-center"
          >
            <p className="text-xl text-zinc-300">
              <span className="text-white font-semibold">That's it.</span> 30-minute kickoff. A few feedback sessions. A final handoff call.
            </p>
            <p className="text-lg text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400 font-semibold mt-2">
              You spend 2-3 hours. You get a production application.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Study */}
      <CaseStudySection />

      {/* Urgency Section */}
      <UrgencySection />

      {/* Investment Section */}
      <InvestmentSection />

      {/* Tech Stack - Hidden for now */}
      {/* <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <TechStackGrid
            title="Built With Modern Technology"
            items={techStack}
            accent="violet"
          />
        </div>
      </section> */}

      {/* Guarantee Section */}
      <GuaranteeSection
        headline="We Deliver. Or We Don't Get Paid."
        guarantee="If we don't deliver a working, deployable application by the agreed deadline, we keep working until we do — at no additional cost."
        details={[
          'No excuses. No change orders. No "well, the scope changed."',
          'We agreed on a deadline. We hit it.',
          "If you're not satisfied with the final result, we refund your investment. You keep everything we built.",
        ]}
        riskStatement="Deadline guaranteed or we work free"
        upsideStatement="A production app in 2 weeks"
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
      />

      {/* Related Services - Hidden for now */}
      {/* <RelatedServicesCarousel
        title="Explore Related Services"
        services={relatedServices}
      /> */}

      {/* Final CTA Section */}
      <CTASection
        headline="Ship in Weeks, Not Quarters"
        subheadline="That app stuck in your backlog? We'll have it live in 2-4 weeks. Scope, timeline, and exact price within 24 hours. No surprises."
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        urgencyText="Your competitors are shipping while you're still planning."
        secondaryText="24-hour response. Fixed price. Guaranteed delivery."
      />
    </main>
  );
};

export default VibeCodePage;
